import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useReducer } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Entry extends Record<PropertyKey, unknown> {
  id: string
  patient: string
  first_name: string
  last_name: string
  email?: string | null
  phone?: string | null
  action: MobileHistoryAction
  author: string
  origin?: string | null
  created_at: string
}

const enum MobileHistoryAction {
  Revoke = 'revoke'
}

let mobileHistoryActionMapper: Record<MobileHistoryAction, string> = {
  [MobileHistoryAction.Revoke]: 'Revoke'
}

let originMapper: Record<string, string> = {
  'member-portal': 'Lucent Health',
  'my-health-365': 'My Health 365',
  'mpower': 'mPower',
  'Compassion': 'Compassion',
  'narus': 'Narus'
}

type QueryKey = [
  'mobile-history',
  SearchParams
]

interface QueryData {
  data: Entry[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }
  | { type: 'origin/set', payload: string[] }
  | { type: 'filters/clear' }

interface SearchParams {
  query: string
  sort: Sort
  pagination: Pagination
  originFilter: string[]
}

let sortKeys = [
  'action',
  'author',
  'created_at',
  'email',
  'name',
  'origin',
  'phone'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    sort: {
      field: 'created_at',
      direction: 'desc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    },
    originFilter: []
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    case 'origin/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          originFilter: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          originFilter: initialState.searchParams.originFilter,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  name: 'last_name'
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'originFilter'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.originFilter.length === 0
  )
}

export function MobileHistoryPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState)
  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'mobile-history',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, sort, pagination, originFilter } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      if (originFilter.length > 0) {
        sp.append('filters.origin', originFilter.join(','))
      }

      let response = await http.getRequest(`/admin/mobile_history?${sp.toString()}`)
      return response
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'created_at',
      header: 'Time',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.created_at)}
          </span>
        )
      }
    },
    {
      name: 'action',
      header: 'Action',
      isSortable: true,
      getValue (entry) {
        return mobileHistoryActionMapper[entry.action] ?? entry.action
      }
    },
    {
      name: 'name',
      header: 'Name',
      isSortable: true,
      getValue (entry) {
        return [ entry.first_name?.trim(), entry.last_name?.trim() ]
          .filter(Boolean)
          .join(' ')
      }
    },
    {
      name: 'email',
      header: 'Email',
      isSortable: true
    },
    {
      name: 'phone',
      header: 'Phone',
      isSortable: true
    },
    {
      name: 'author',
      header: 'Author',
      isSortable: true
    },
    {
      name: 'origin',
      header: 'Origin',
      isSortable: true,
      getValue (entry) {
        return entry.origin ? (originMapper[entry.origin] ?? entry.origin) : '-'
      }
    }
  ], [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Mobile History</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by name, email, phone, action, author and origin'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No mobile history entries found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        filters={[
          {
            name: 'origin',
            value: state.searchParams.originFilter,
            options: [
              { value: 'member-portal', label: 'Lucent Health' },
              { value: 'my-health-365', label: 'My Health 365' },
              { value: 'mpower', label: 'mPower' },
              { value: 'Compassion', label: 'Compassion' },
              { value: 'narus', label: 'Narus' }
            ]
          }
        ]}
        columns={columns}
        sort={state.searchParams.sort}
        pagination={pagination}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={(field, values) => {
          if (field === 'origin') {
            dispatch({
              type: 'origin/set',
              payload: values as string[]
            })
          }
        }}
        onFiltersClear={handleFiltersClear}
      />
    </div>
  )
}
